import { useEffect, useRef } from "react";
import Config from "@/common/config";
import { useAuthState } from "@/contexts/auth/auth.provider";
import logger from "@/console";
import { useRouter } from "next/router";
import { useLazyQuery } from "@apollo/client";
import { GET_AUTH_HMAC } from "@/graphql/queries/user";

export const INTERCOM_EVENT = "onintercomeevent";

export function Intercom() {
  const { user, token } = useAuthState();
  const router = useRouter();
  const [getHash] = useLazyQuery(GET_AUTH_HMAC, { context: { token } });
  const loaderDone = useRef(false);
  const requestToOpen = useRef(false);
  const booted = useRef(false);

  useEffect(() => {
    window.INTERCOM_APP_ID = Config.INTERCOM_APP_ID;
    const userData = user
      ? {
          app_id: window.INTERCOM_APP_ID,
          email: user.email,
          name: user.name,
          user_id: user.user_id,
          phone: user.phone,
          user_hash: user.web_intercom_hmac,
          avatar: {
            type: "avatar",
            image_url: user.avatar,
          },
        }
      : {
          app_id: window.INTERCOM_APP_ID,
        };

    function ls(ev) {
      const { type, ...extra } = ev.detail || {};

      if (type === "shutdown") {
        logger.log("Destroying Intercom Session", ev.detail, userData);
        if (typeof window.Intercom === "function") {
          booted.current = false;
          return window.Intercom("shutdown");
        }
      }

      if (user && booted.current) {
        if (type === "show") return window.Intercom("show");
      }

      logger.log("Loading Intercom widget");
      initInterCome(async () => {
        logger.log("Loading Intercom widget Done");
        loaderDone.current = true;
        if (user) {
          if (type !== "load") {
            if (!user.web_intercom_hmac) {
              const { data } = await getHash({ context: { token } });
              userData.user_hash = data?.User?.get?.web_intercom_hmac;
            }
            window.Intercom("boot", userData);

            booted.current = true;
            logger.log("Intercom loaded and booted", ev.detail, userData);
          }
          if (type === "show") {
            logger.log("Showing Intercom widget");
            window.Intercom("show");
          }
        }
      });

      if (type === "showArticle") {
        if (!booted.current) {
          window.Intercom("boot", userData);
          booted.current = true;
        }
        return window.Intercom("showArticle", extra.id);
      }

      if (!user && type !== "load") {
        requestToOpen.current = true;
        return router.push("/login");
      }

      if (type === "showArticle") {
        return window.Intercom("showArticle", extra.id);
      }
    }

    if (user && requestToOpen.current) {
      requestToOpen.current = false;
      logger.log("requestToOpen Intercom  After Login ", userData);
      if (!booted.current) {
        window.Intercom("boot", userData);
        booted.current = true;
      }
      window.Intercom("show");
    }
    if (!user && loaderDone.current) {
      window.Intercom("shutdown");
    }
    document.addEventListener(INTERCOM_EVENT, ls);
    return () => document.removeEventListener(INTERCOM_EVENT, ls);
  }, [user, token]);
}

export function initInterCome(onLoad) {
  var w = window;
  var ic = w.Intercom;
  if (typeof ic === "function") {
    ic("reattach_activator");
    ic("update", w.intercomSettings);
    onLoad();
  } else {
    var d = document;
    var i = function () {
      i.c(arguments);
    };
    i.q = [];
    i.c = function (args) {
      i.q.push(args);
    };
    w.Intercom = i;
    const l = function () {
      if (window.intercomeLoaded) return onLoad();
      if (document.getElementById("intercom")) return;
      const s = d.createElement("script");
      s.type = "text/javascript";
      s.id = "intercom";
      s.async = true;
      s.src = "https://widget.intercom.io/widget/" + window.INTERCOM_APP_ID;
      const x = d.getElementsByTagName("script")[0];
      x.parentNode.insertBefore(s, x);
      window.intercomeLoaded = true;
      onLoad();
    };
    if (document.readyState === "complete") {
      l();
    } else if (w.attachEvent) {
      w.attachEvent("onload", l);
    } else {
      w.addEventListener("load", l, false);
    }
  }
}
